/** Range Development (Demo Mode only, Range Planning) - the guided journey for building and
 * approving a single site-specific food range, from service brief through to go-live. Reads
 * the same Range Overview mock data (src/app/services/api/rangeOverview) so step status and
 * blockers stay consistent with what the Range Overview control tower shows for the same
 * range. No real backend exists yet for this concept - see Range Overview for context on why. */

import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert, Box, Button, Chip, Divider, Grid, MenuItem, Stack, Step, StepContent, StepLabel, Stepper, SvgIcon, TextField, Typography,
} from '@mui/material';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { Section } from '../../components/ui/Section';
import { UserContext } from '../../components/shared/useUser';
import { ranges } from '../../mocks/data/rangesData';
import { menus } from '../../mocks/data/menusData';
import { mockRecipesV2 } from '../../mocks/data/recipeV2Data';
import { ArrowRight } from '../../assets';
import { ReadinessStatus, RangeStatus } from '../../app/services/api/rangeOverview/rangeOverviewTypes';
import AIChecksPanel from '../../components/shared/aiChecks/AIChecksPanel';
import { aiChecksClient } from '../../app/services/api/aiChecks/aiChecksEngine';
import { AICheckFinding } from '../../app/services/api/aiChecks/aiChecksTypes';
import DataCompletenessChip from '../RecipeV2/components/DataCompletenessChip';

type StepState = 'complete' | 'attention' | 'blocked';

const STATE_COLOR: Record<StepState, 'success' | 'warning' | 'error'> = {
  complete: 'success',
  attention: 'warning',
  blocked: 'error',
};

const RangeDevelopmentPage = () => {
  const { t } = useTranslation('rangeDevelopment');
  const navigate = useNavigate();
  const { hasPermissionTo } = useContext(UserContext);
  const canApprove = hasPermissionTo(['ProcurementRolloutApprove']);

  const [rangeId, setRangeId] = useState(ranges[0]?.id ?? 0);
  const range = useMemo(() => ranges.find((r) => r.id === rangeId) ?? ranges[0], [rangeId]);

  // The single Menu that backs this range (see rangesData.ts - ranges 1-3 line up 1:1 with
  // Menus of the same id; demo-only ranges 4+ have no menuId yet).
  const menu = useMemo(() => (range?.menuId ? menus.find((m) => m.id === range.menuId) ?? null : null), [range]);

  // Flat list of every recipe/dish served on that menu, deduped, so the panel can link straight
  // through to each recipe's RecipeV2 detail page alongside the menu link itself. Display name
  // and data-completeness are resolved from the RecipeV2 catalogue, not stored on the dish.
  const menuDishes = useMemo(() => {
    if (!menu) return [];
    const recipeIds = new Set<number>();
    menu.courses.forEach((c) => c.dishes.forEach((d) => recipeIds.add(d.recipeId)));
    (menu.dayStructure ?? []).forEach((day) => day.courses.forEach((c) => c.dishes.forEach((d) => recipeIds.add(d.recipeId))));
    return Array.from(recipeIds, (recipeId) => {
      const recipe = mockRecipesV2.find((r) => r.id === recipeId);
      return {
        recipeId,
        recipeName: recipe?.customerFacingName ?? recipe?.description ?? t('unknownRecipe', 'Unknown recipe'),
        dataCompleteness: recipe?.dataCompleteness,
      };
    });
  }, [menu, t]);

  // AI Recipe Checks findings scoped to this range's menu (and, transitively, every recipe used
  // on that menu) - same aiChecksClient.getFindingsForMenu query Menu Detail uses, so a finding
  // here links through to /menus/:id or /store/recipes-v2/:id at the appropriate granularity.
  const [aiChecksFindings, setAiChecksFindings] = useState<AICheckFinding[]>([]);

  useEffect(() => {
    if (range?.menuId) {
      aiChecksClient.getFindingsForMenu(range.menuId).then(setAiChecksFindings);
    } else {
      setAiChecksFindings([]);
    }
  }, [range]);

  // lazy: step status is derived from the same cost/allergen/carbon readiness fields Range
  // Overview already tracks, rather than modelling every journey step independently - upgrade
  // path: back each step with its own real validation once Menus/Recipes/Items own that data.
  const steps = useMemo(() => {
    if (!range) return [];
    const readinessToState = (status: ReadinessStatus): StepState => (
      status === ReadinessStatus.Blocked ? 'blocked' : status === ReadinessStatus.Attention ? 'attention' : 'complete'
    );

    return [
      { key: 'serviceBrief', state: 'complete' as StepState },
      { key: 'siteCapability', state: 'complete' as StepState },
      { key: 'rangeStrategy', state: (range.status === RangeStatus.Draft ? 'attention' : 'complete') as StepState },
      { key: 'menuCycles', state: (range.menuId ? 'complete' : 'attention') as StepState },
      { key: 'recipes', state: (range.menuId ? 'complete' : 'attention') as StepState },
      { key: 'itemsIngredients', state: readinessToState(range.allergenStatus) },
      { key: 'allergensDietary', state: readinessToState(range.allergenStatus) },
      { key: 'nutrition', state: 'attention' as StepState },
      { key: 'sustainability', state: readinessToState(range.carbonStatus) },
      { key: 'costBudgetMargin', state: readinessToState(range.costStatus) },
      { key: 'supplierAvailability', state: 'complete' as StepState },
      { key: 'operationalFeasibility', state: 'complete' as StepState },
      { key: 'approval', state: (range.status === RangeStatus.Live || range.status === RangeStatus.Approved ? 'complete' : 'attention') as StepState },
    ];
  }, [range]);

  const blockers = steps.filter((s) => s.state === 'blocked');
  const canGoLive = blockers.length === 0;
  const activeStep = steps.findIndex((s) => s.state !== 'complete');

  return (
    <PageArea>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 4 }}>
        <Box>
          <PageTitle>{t('title', 'Range Development')}</PageTitle>
          <Typography variant="textSM"
            color="text.secondary"
            sx={{ mt: 1, maxWidth: 640 }}>
            {t('intro', 'Build an approved, feasible, compliant and commercially viable site-specific range, step by step.')}
          </Typography>
        </Box>
        <TextField
          select
          label={t('rangeSelector', 'Range')}
          value={range?.id ?? ''}
          onChange={(e) => setRangeId(Number(e.target.value))}
          sx={{ minWidth: 280 }}
        >
          {ranges.map((r) => (
            <MenuItem key={r.id}
              value={r.id}>{r.name}</MenuItem>
          ))}
        </TextField>
      </Box>

      {aiChecksFindings.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <AIChecksPanel findings={aiChecksFindings} />
        </Box>
      )}

      {range?.description && (
        <Section style={{ marginBottom: 32 }}>
          <Box sx={{ p: 5 }}>
            <Stack direction="row"
              spacing={1}
              flexWrap="wrap"
              sx={{ mb: 2 }}>
              {range.season && <Chip label={range.season}
                size="small"
                color="secondary" />}
              {range.pricePosition && <Chip label={range.pricePosition}
                size="small"
                variant="outlined" />}
              {range.serviceModel?.map((model) => (
                <Chip key={model}
                  label={model}
                  size="small"
                  variant="outlined" />
              ))}
            </Stack>
            <Typography variant="textMD"
              sx={{ mb: 1, fontStyle: 'italic' }}>
              {range.description}
            </Typography>
            {range.targetAudience && (
              <Typography variant="textSM"
                color="text.secondary"
                sx={{ mb: 3 }}>
                {t('targetAudience', 'Designed for: {{audience}}', { audience: range.targetAudience })}
                {range.owner ? ` · ${t('owner', 'Owner: {{owner}}', { owner: range.owner })}` : ''}
              </Typography>
            )}
            <Grid container
              spacing={4}>
              {range.principles && range.principles.length > 0 && (
                <Grid item
                  xs={12}
                  md={6}>
                  <Typography variant="textSM"
                    fontWeight={600}
                    sx={{ mb: 1 }}>
                    {t('principles', 'Chef-led principles')}
                  </Typography>
                  <Stack component="ul"
                    spacing={0.5}
                    sx={{ pl: 3, m: 0 }}>
                    {range.principles.map((principle) => (
                      <Typography component="li"
                        variant="textSM"
                        color="text.secondary"
                        key={principle}>
                        {principle}
                      </Typography>
                    ))}
                  </Stack>
                </Grid>
              )}
              {range.targets && (
                <Grid item
                  xs={12}
                  md={6}>
                  <Typography variant="textSM"
                    fontWeight={600}
                    sx={{ mb: 1 }}>
                    {t('targets', 'Range targets')}
                  </Typography>
                  <Grid container
                    spacing={2}>
                    <Grid item
                      xs={6}>
                      <Typography variant="textXS"
                        color="text.secondary">{t('targetAvgPrice', 'Avg. selling price')}</Typography>
                      <Typography variant="textSM"
                        fontWeight={600}>£{range.targets.averageSellingPrice.toFixed(2)}</Typography>
                    </Grid>
                    <Grid item
                      xs={6}>
                      <Typography variant="textXS"
                        color="text.secondary">{t('targetFoodCost', 'Avg. food cost')}</Typography>
                      <Typography variant="textSM"
                        fontWeight={600}>{range.targets.averageFoodCostPercentage}%</Typography>
                    </Grid>
                    <Grid item
                      xs={6}>
                      <Typography variant="textXS"
                        color="text.secondary">{t('targetGrossMargin', 'Gross margin')}</Typography>
                      <Typography variant="textSM"
                        fontWeight={600}>{range.targets.grossMarginPercentage}%</Typography>
                    </Grid>
                    <Grid item
                      xs={6}>
                      <Typography variant="textXS"
                        color="text.secondary">{t('targetPlantBased', 'Plant-based dishes')}</Typography>
                      <Typography variant="textSM"
                        fontWeight={600}>{range.targets.plantBasedDishPercentage}%</Typography>
                    </Grid>
                    <Grid item
                      xs={6}>
                      <Typography variant="textXS"
                        color="text.secondary">{t('targetBritishSourced', 'British-sourced ingredients')}</Typography>
                      <Typography variant="textSM"
                        fontWeight={600}>{range.targets.britishSourcedIngredientPercentage}%</Typography>
                    </Grid>
                    <Grid item
                      xs={6}>
                      <Typography variant="textXS"
                        color="text.secondary">{t('targetCustomerSatisfaction', 'Customer satisfaction')}</Typography>
                      <Typography variant="textSM"
                        fontWeight={600}>{range.targets.customerSatisfactionScore.toFixed(1)} / 5</Typography>
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Box>
        </Section>
      )}

      {!range ? (
        <Alert severity="info">{t('noRanges', 'No ranges available.')}</Alert>
      ) : (
        <Grid container
          spacing={4}>
          <Grid item
            xs={12}
            md={8}>
            <Section>
              <Box sx={{ p: 4 }}>
                <Stepper activeStep={activeStep === -1 ? steps.length : activeStep}
                  orientation="vertical">
                  {steps.map((step) => (
                    <Step key={step.key}
                      completed={step.state === 'complete'}>
                      <StepLabel
                        error={step.state === 'blocked'}
                        optional={
                          <Chip label={t(`state.${step.state}`, step.state)}
                            color={STATE_COLOR[step.state]}
                            size="small"
                            variant="outlined" />
                        }
                      >
                        {t(`step.${step.key}`, step.key)}
                      </StepLabel>
                      <StepContent>
                        <Typography variant="textSM"
                          color="text.secondary">
                          {t(`stepDesc.${step.key}`, '')}
                        </Typography>
                      </StepContent>
                    </Step>
                  ))}
                </Stepper>
              </Box>
            </Section>
          </Grid>

          <Grid item
            xs={12}
            md={4}>
            <Section>
              <Box sx={{ p: 4 }}>
                <Typography variant="textSM"
                  fontWeight={600}
                  sx={{ mb: 2 }}>
                  {t('approvalTitle', 'Approval')}
                </Typography>
                {blockers.length > 0 ? (
                  <Alert severity="error"
                    sx={{ mb: 3 }}>
                    {t('blockersFound', '{{count}} critical blocker(s) must be resolved before this range can go live.', { count: blockers.length })}
                  </Alert>
                ) : (
                  <Alert severity="success"
                    sx={{ mb: 3 }}>
                    {t('noBlockers', 'No critical blockers - this range is ready for approval.')}
                  </Alert>
                )}
                <Button variant="primary"
                  disabled={!canGoLive || !canApprove}
                  fullWidth>
                  {t('approveButton', 'Approve for go-live')}
                </Button>
                {!canApprove && (
                  <Typography variant="textXS"
                    color="text.secondary"
                    sx={{ mt: 1 }}>
                    {t('requiresRole', 'Requires the Operations demo role to approve.')}
                  </Typography>
                )}
              </Box>
            </Section>

            <Section style={{ marginTop: 16 }}>
              <Box sx={{ p: 4 }}>
                <Typography variant="textSM"
                  fontWeight={600}
                  sx={{ mb: 2 }}>
                  {t('underlyingMenuTitle', 'Underlying Menu')}
                </Typography>
                {!menu ? (
                  <Typography variant="textSM"
                    color="text.secondary">
                    {t('noUnderlyingMenu', 'No menu is linked to this range yet.')}
                  </Typography>
                ) : (
                  <Stack spacing={3}>
                    <Box
                      onClick={() => navigate(`/menus/${menu.id}`)}
                      sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                    >
                      <Typography variant="textSM"
                        fontWeight={600}
                        color="primary.main">
                        {menu.name}
                      </Typography>
                      <SvgIcon fontSize="small"
                        color="action"><ArrowRight /></SvgIcon>
                    </Box>
                    <Divider />
                    <Box>
                      <Typography variant="textXS"
                        color="text.secondary"
                        sx={{ mb: 1 }}>
                        {t('recipesOnMenu', 'Recipes on this menu')}
                      </Typography>
                      {menuDishes.length === 0 ? (
                        <Typography variant="textSM"
                          color="text.secondary">
                          {t('noRecipesOnMenu', 'No recipes on this menu yet.')}
                        </Typography>
                      ) : (
                        <Stack spacing={1}>
                          {menuDishes.map((dish) => (
                            <Box
                              key={dish.recipeId}
                              onClick={() => navigate(`/store/recipes-v2/${dish.recipeId}`)}
                              sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
                            >
                              <Stack direction="row"
                                spacing={1}
                                alignItems="center">
                                <Typography variant="textSM"
                                  color="primary.main">
                                  {dish.recipeName}
                                </Typography>
                                <DataCompletenessChip completeness={dish.dataCompleteness} />
                              </Stack>
                              <SvgIcon fontSize="small"
                                color="action"><ArrowRight /></SvgIcon>
                            </Box>
                          ))}
                        </Stack>
                      )}
                    </Box>
                  </Stack>
                )}
              </Box>
            </Section>
          </Grid>
        </Grid>
      )}
    </PageArea>
  );
};

export default RangeDevelopmentPage;
